import {
  ContentsItem,
  getInsuranceTypeDisplayName,
  getOccupancyDisplayName,
  MobilePhone,
  OwnerOccupancyCode,
  ResidentialCoverage,
} from '@/interfaces';
import { formatCurrency } from '@/utils/text-transforms';
import { ReferenceDataServices as _refData } from '@/services/store/reference-data.services';
import {
  getAdditionalText,
  getPdsOrSpdsVersion,
  InsuranceDocumentType,
  policyDetailsCheckIcon,
} from './PolicyDetailsHelperMapper';

/**
 * Returns the populated SummaryItems with DOM ready text.
 * @param coverage The Home insurance coverage
 * @param content The props.content for the page
 */
export async function mapAsResidentialCoverage(
  coverage: ResidentialCoverage,
  content: any,
  dictionary: Record<string, string>
): Promise<any> {
  const summary = {} as any;
  // Policy details
  summary.insuranceTypeDisplayName = getInsuranceTypeDisplayName(dictionary, coverage.coverageCode);
  summary.insuredAddress = coverage.riskAddress.formattedLong;
  summary.occupancy = getOccupancyDisplayName(coverage.ownerOccupancyCode as OwnerOccupancyCode);
  summary.mortgagee = getMortgagee(coverage);

  // Building sum insured
  summary.sumInsured = formatCurrency(coverage.buildingSumInsured, 2);

  // Contents sum insured
  summary.mobilePhones = getMobilePhones(coverage.mobilePhones)?.join('<br/>');
  summary.groupedItemsAwayFromHome = await getGroupedItemsAwayFromHome(coverage);
  summary.individualItemsAwayFromHome = getIndividualItemsAwayFromHome(coverage.portableItems);
  summary.specifiedItems = getSpecifiedItems(coverage)?.join('<br/>');
  summary.generalContents = formatCurrency(coverage.generalContentsAmount);
  summary.totalContents = formatCurrency(coverage.totalContentsAmount);

  // Applicable excesses
  summary.buildingExcess = coverage.buildingExcessCode && formatCurrency(Number(coverage.buildingExcessCode));
  summary.contentsExcess = coverage.contentsExcessCode && formatCurrency(Number(coverage.contentsExcessCode));

  // Optional benefits
  summary.advancedCover = getAdvancedCover(coverage, content);
  summary.motorBurnout = getMotorBurnout(coverage, content);
  summary.petCover = getPetCover(coverage, content);
  summary.smallBusinessContents = getSmallBusinessContents(coverage);
  summary.increasedLandlordFixturesAndFittings = getIncreasedLandlordFixturesAndFittings(coverage);

  // Insurance documents
  summary.pdsVersion = await getPdsOrSpdsVersion(coverage, InsuranceDocumentType.PDS);
  summary.spdsVersion = await getPdsOrSpdsVersion(coverage, InsuranceDocumentType.SPDS);

  // Your discounts is handled by a slot in ReviewSummary
  //summary.yourDiscounts = getYourDiscounts(coverage);

  return summary;
}

function getMortgagee(coverage: ResidentialCoverage): string | null {
  if (!coverage.mortgagee || coverage.mortgagee.length === 0) {
    return null;
  }
  return coverage.mortgagee.join('<br/>');
}

async function getGroupedItemsAwayFromHome(coverage: ResidentialCoverage): Promise<string> {
  try {
    const response = await _refData.getHomeAndContentsGroupCoverPriceBands(coverage.coverageCode);
    if (response?.values && response.values.length > 0) {
      const result = response.values.find((x: any) => x.code === coverage.groupCoverCode)?.name;
      return result || 'No Group Cover';
    }
    return 'No Group Cover';
  } catch (error) {
    console.error('err', error);
    return 'No Group Cover';
  }
}

function getSpecifiedItems(coverage: ResidentialCoverage): string[] {
  if (!coverage.homeItems || coverage.homeItems?.length === 0) {
    return [];
  }

  const specificItems = [];
  for (const item of coverage.homeItems) {
    specificItems.push(`${item.itemDescription} - ${formatCurrency(item.value)}`);
  }
  return specificItems;
}

function getIncreasedLandlordFixturesAndFittings(coverage: ResidentialCoverage): string | null {
  if (coverage.landlordFixturesAndFittings && coverage.landlordFixturesAndFittings > 0) {
    return policyDetailsCheckIcon + 'Home - ' + formatCurrency(coverage.landlordFixturesAndFittings);
  }
  return null;
}

function getSmallBusinessContents(coverage: ResidentialCoverage): string | null {
  if (coverage.hasSmallBusinessContentsCover) {
    return policyDetailsCheckIcon + 'Added';
  }
  return null;
}

function getPetCover(coverage: ResidentialCoverage, content: any): string | null {
  if (!coverage.petsCovered || coverage.petsCovered === 0) {
    return null;
  }

  const petAdditionalText = getAdditionalText(content, 'optionalBenefitsPetCoverText');
  return `${policyDetailsCheckIcon}${coverage.petsCovered} Pet${coverage.petsCovered === 1 ? '' : 's'}${petAdditionalText}`;
}

function getAdvancedCover(coverage: ResidentialCoverage, content: any): string | null {
  const { hasBuildingAdvancedCover, hasContentsAdvancedCover } = coverage;

  if (hasBuildingAdvancedCover && hasContentsAdvancedCover) {
    return (
      policyDetailsCheckIcon +
      'Home and Contents' +
      getAdditionalText(content, 'optionalBenefitsAdvancedCoverHomeAndContentsText')
    );
  } else if (hasBuildingAdvancedCover) {
    return policyDetailsCheckIcon + 'Home' + getAdditionalText(content, 'optionalBenefitsAdvancedCoverHomeText');
  } else if (hasContentsAdvancedCover) {
    return (
      policyDetailsCheckIcon + 'Contents' + getAdditionalText(content, 'optionalBenefitsAdvancedCoverContentsText')
    );
  }

  return null;
}

function getMotorBurnout(coverage: ResidentialCoverage, content: any): string | null {
  const { hasBuildingMotorBurnout, hasContentsMotorBurnout } = coverage;

  if (hasBuildingMotorBurnout && hasContentsMotorBurnout) {
    return (
      policyDetailsCheckIcon +
      'Home and Contents' +
      getAdditionalText(content, 'optionalBenefitsMotorBurnoutHomeAndContentsText')
    );
  } else if (hasBuildingMotorBurnout) {
    return policyDetailsCheckIcon + 'Home' + getAdditionalText(content, 'optionalBenefitsMotorBurnoutHomeText');
  } else if (hasContentsMotorBurnout) {
    return policyDetailsCheckIcon + 'Contents' + getAdditionalText(content, 'optionalBenefitsMotorBurnoutContentsText');
  }
  return null;
}

function getIndividualItemsAwayFromHome(contentItems: ContentsItem[] | null | undefined): string[] {
  if (contentItems === null || contentItems === undefined || contentItems.length === 0) {
    return [];
  }

  const individualItemsAwayFromHome = [];
  for (const item of contentItems) {
    individualItemsAwayFromHome.push(`${item.itemDescription} - ${formatCurrency(item.value)}`);
  }
  return individualItemsAwayFromHome;
}

function getMobilePhones(mobilePhones?: MobilePhone[] | null): string[] | null {
  if (mobilePhones === null || mobilePhones === undefined || mobilePhones.length === 0) {
    return [];
  }

  const result = [];
  for (const phone of mobilePhones) {
    result.push(`${phone.description} - ${formatCurrency(phone.value)}`);
  }
  return result;
}
